import { z } from 'zod';

import {
  type AnswerReading,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import {
  type DgContent,
  type DgQuestion,
  type DgStimulus,
  type StalenessWindow,
  dgOptionWording,
} from './artifacts.ts';
import { guideMinutes } from './duration.ts';
import {
  MAX_DG_OPTION_CHARS,
  MAX_DG_OPTIONS_DRAFTED,
  MAX_DG_QUESTION_CHARS,
  MAX_DG_SECTION_TITLE_CHARS,
  MIN_DG_OPTIONS,
} from './guide.ts';
import { type GuideShape, studyTypeIdSchema } from './study-types.ts';
import type { ArtifactVersionReason } from './version-reasons.ts';

/**
 * The queue a guide is drafted on. The API process stores the request and enqueues it; the worker,
 * which is the only process holding a model, answers it.
 */
export const QUEUE_DG_DRAFT = 'dg.draft';

/**
 * How often the Guide step re-reads a pending request, and how long it waits before it offers "Try
 * again". Placeholder policy values. The wait holds a draft, a second call when its answer broke a
 * rule, its coverage check, one repair of the gap and uncertain lines that check found and a check
 * of the repair (`draftGuideCovered`), each call given only what is left of it.
 */
export const DG_DRAFT_POLL_MS = 1000;
export const DG_DRAFT_WAIT_MS = 120_000;

/**
 * What a draft was asked from: the approved research questions it serves, the chosen Goal it is
 * paced to, the Study Type and template it is shaped by, the confirmed Materials it reads, and the
 * version of the guide it would write over (`null` for a first draft). A settle writes nothing onto
 * inputs that moved.
 */
export const dgDraftInputsSchema = z.strictObject({
  rqs_version: z.int().positive(),
  goal_version: z.int().positive(),
  goal_option_id: z.uuid(),
  study_type_id: studyTypeIdSchema,
  template_version: z.int().positive(),
  materials_version: z.int().positive(),
  dg_version: z.int().positive().nullable(),
});
export type DgDraftInputs = z.infer<typeof dgDraftInputsSchema>;

export const DG_DRAFT_STATUSES = ['pending', 'ready', 'unreadable', 'unavailable'] as const;
export type DgDraftStatus = (typeof DG_DRAFT_STATUSES)[number];

/**
 * The latest request for a drafted guide.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the worker drafted the guide, which is the study's current version.
 * - `unreadable` — the model answered, and its answer was not a whole guide in the template's shape.
 * - `unavailable` — no answer could be had: no model is configured, the day's budget is spent, the
 *   call failed, or the study moved while it was being drafted.
 */
export const dgDraftRequestSchema = z.strictObject({
  id: z.uuid(),
  status: z.enum(DG_DRAFT_STATUSES),
  requested_at: z.iso.datetime(),
  inputs: dgDraftInputsSchema,
});
export type DgDraftRequest = z.infer<typeof dgDraftRequestSchema>;

export const DG_DRAFT_SCHEMA_VERSION = 1;

/**
 * What `studies.dg_draft` holds: the latest drafting request, or none. The drafted guide itself is a
 * guide version, never held here.
 */
export const storedDgDraftSchema = z.strictObject({
  schema_version: z.literal(DG_DRAFT_SCHEMA_VERSION),
  request: dgDraftRequestSchema.nullable(),
});
export type StoredDgDraft = z.infer<typeof storedDgDraftSchema>;

/**
 * The stored request, or nothing. A shape this build cannot read is treated as no request rather
 * than an error: the study's next request replaces it.
 */
export const parseStoredDgDraft = (stored: unknown): StoredDgDraft | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedDgDraftSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** Whether two sets of inputs describe the same study. */
export const sameDgDraftInputs = (left: DgDraftInputs, right: DgDraftInputs): boolean =>
  left.rqs_version === right.rqs_version &&
  left.goal_version === right.goal_version &&
  left.goal_option_id === right.goal_option_id &&
  left.study_type_id === right.study_type_id &&
  left.template_version === right.template_version &&
  left.materials_version === right.materials_version &&
  left.dg_version === right.dg_version;

/** As much of a study's guide as deciding whether to draft it reads. */
export interface DgDraftFact {
  staleness: readonly StalenessWindow[];
  /**
   * Whether the coverage check of the guide as it stands, against the research questions as they
   * stand, found a research question nothing in it answers or one it is not sure it answers; see
   * `dgCoverageUnanswered`. Absent reads as no.
   */
  coverageUnanswered?: boolean;
}

/**
 * Whether the study needs its guide drafted from scratch: it has none. A guide that exists is never
 * drafted over unasked, so a guide the researcher approved or reworded keeps its wording.
 */
export const dgDraftNeeded = (dg: DgDraftFact | null): boolean => dg === null;

/**
 * Whether the study's guide needs reconciling: it is waiting on a change above it — a changed Goal,
 * changed Materials or changed research questions. Reconciling writes the smallest change that
 * answers each, keeping every question it does not need to touch as it is stored.
 */
export const dgReconcileNeeded = (dg: DgDraftFact | null): boolean =>
  dg !== null && dg.staleness.length > 0;

/**
 * What a request for the guide is answered with, read off the guide as it stands: `draft` for a
 * study with no guide, `reconcile` for a guide waiting on a change above it, and `repair` for a
 * guide waiting on nothing — the only request such a guide has is the researcher's "Redraft the
 * guide" over a coverage gap or uncertain line (`dgRedraftOffered`), answered by changing what fails
 * the research questions those lines name and keeping every other question, and the researcher's
 * wording, as it is stored. Never stored: the windows change only when an upstream version does,
 * and every upstream version is in a request's inputs, so the mode is a function of the inputs.
 */
export type DgDraftMode = 'draft' | 'reconcile' | 'repair';

export const dgDraftMode = (dg: DgDraftFact | null): DgDraftMode =>
  dg === null ? 'draft' : dgReconcileNeeded(dg) ? 'reconcile' : 'repair';

/**
 * Whether the step's "Redraft the guide" may ask for a guide that needs nothing: one waiting on no
 * change whose current coverage check found a gap or an uncertain line. It is answered as a
 * `repair`, which may reword a question the researcher worded, so it is asked for only when they ask.
 */
export const dgRedraftOffered = (dg: DgDraftFact | null): boolean =>
  dg !== null && dg.staleness.length === 0 && dg.coverageUnanswered === true;

/**
 * Whether a study needs a new request for its current inputs, rather than the one it holds.
 *
 * Only while the guide needs drafting or reconciling (`dgDraftNeeded`, `dgReconcileNeeded`), and not
 * while a request for these inputs is pending and younger than `DG_DRAFT_WAIT_MS`. A pending request
 * older than that is one the step has given up waiting on — its job was lost with a worker, expired,
 * or never ran — and since the queue retries nothing, treating it as still in hand would leave the
 * step waiting on nothing.
 */
export const dgDraftRequestNeeded = ({
  request,
  inputs,
  dg,
  now,
}: {
  request: DgDraftRequest | null;
  inputs: DgDraftInputs;
  dg: DgDraftFact | null;
  now: Date;
}): boolean => {
  if (!dgDraftNeeded(dg) && !dgReconcileNeeded(dg)) {
    return false;
  }

  const waiting =
    request !== null &&
    request.status === 'pending' &&
    sameDgDraftInputs(request.inputs, inputs) &&
    now.getTime() - Date.parse(request.requested_at) < DG_DRAFT_WAIT_MS;

  return !waiting;
};

/** Why one item of a reconciled or repaired guide changed, as the model said; see `ChangeNotesInput`. */
export interface DgChangeReason {
  id: string;
  reason: string;
}

/**
 * How a request is settled: every status but the one it starts in. A `ready` answer names the mode
 * it answers (`dgDraftMode`), which the settle holds against the guide's mode under the lock. A
 * reconciliation or a repair carries `reasons`, the reason it gave for each question or section it
 * changed, added or removed; a draft carries none.
 */
export type DgDraftOutcome =
  | {
      status: 'ready';
      content: DgContent;
      mode: DgDraftMode;
      reasons?: readonly DgChangeReason[] | undefined;
    }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const dgDraftPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type DgDraftPayload = z.infer<typeof dgDraftPayloadSchema>;

const words = (max: number) => z.string().trim().min(1).max(max);

/**
 * The drafter's answer format's shape, with `image` the type of a question's image: every key
 * required, `image` `null` on a question that shows none, and no bound. A question is either kind's
 * object, which the provider takes as `anyOf`.
 */
const guideAnswerShape = <Image extends z.ZodType<number | null>>(image: Image) => {
  const open = z.strictObject({
    kind: z.literal('open'),
    text: z.string(),
    serves: z.array(z.int()),
    image,
  });
  const poll = z.strictObject({
    kind: z.literal('poll'),
    text: z.string(),
    options: z.array(z.string()),
    serves: z.array(z.int()),
    image,
  });
  const section = z.strictObject({
    title: z.string(),
    questions: z.array(z.union([open, poll])),
  });

  return { open, poll, section, answer: z.strictObject({ sections: z.array(section) }) };
};

/**
 * The drafter's answer format's shape for a study that may show `imageCount` images, as the
 * provider is asked to hold it (`guideAnswerWire`): with none to show, every question's `image` is
 * `null`, so no answer can name one. The parser holds every bound, the template's shape and each
 * image number to one it may show.
 */
export const guideAnswerWireShape = (imageCount: number) =>
  guideAnswerShape(imageCount === 0 ? z.null() : z.int().nullable()).answer;

/** The wire a guide draft that may show `imageCount` images answers under. */
export const guideAnswerWire = (imageCount: number): AnswerWire =>
  answerWire('guide_answer', guideAnswerWireShape(imageCount));

const parserShape = guideAnswerShape(z.int().nullable());

/** Research questions as the drafter names them: 1-based positions in the list it was shown. */
const servesSchema = z.array(z.int().positive()).min(1);

/**
 * An image the drafter shows before a question: its 1-based number among those it may show. `null`
 * and a missing key both mean the question shows none, read as `undefined`.
 */
const imageSchema = z
  .int()
  .positive()
  .nullable()
  .optional()
  .transform((image) => image ?? undefined);

/**
 * One question as the drafter writes it. It names no id and no author and has only the two kinds a
 * drafted guide may hold, so a several-answer poll has no way to be written and anything else the
 * model adds is refused rather than dropped. An image is named by its number, never by a file id.
 */
const guideAnswerQuestionSchema = z.discriminatedUnion('kind', [
  parserShape.open.extend({
    text: words(MAX_DG_QUESTION_CHARS),
    serves: servesSchema,
    image: imageSchema,
  }),
  parserShape.poll.extend({
    text: words(MAX_DG_QUESTION_CHARS),
    options: z.array(words(MAX_DG_OPTION_CHARS)).min(MIN_DG_OPTIONS).max(MAX_DG_OPTIONS_DRAFTED),
    serves: servesSchema,
    image: imageSchema,
  }),
]);
type GuideAnswerQuestion = z.infer<typeof guideAnswerQuestionSchema>;

const guideAnswerSchema = parserShape.answer.extend({
  sections: z
    .array(
      parserShape.section.extend({
        title: words(MAX_DG_SECTION_TITLE_CHARS),
        questions: z.array(guideAnswerQuestionSchema).min(1),
      }),
    )
    .min(1),
});
type GuideAnswer = z.infer<typeof guideAnswerSchema>;
/** An answer as it is written, before `null` images are read as none. */
type GuideAnswerDocument = z.input<typeof guideAnswerSchema>;

/** The document a drafter answers with, as its instructions show it. */
export const GUIDE_ANSWER_EXAMPLE =
  '{"sections": [{"title": "<section title>", "questions": [{"kind": "open", "text": "<question>", "serves": [<research question number>, ...], "image": null}, {"kind": "poll", "text": "<question>", "options": ["<answer>", ...], "serves": [<research question number>, ...], "image": null}]}, ...]}';

/**
 * The rules a guide draft's answer can break, each the reason `parseGuideAnswer` refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a field outside its bounds included;
 * - `sections`: not the sections the template's shape asks for;
 * - `image`: an image past the last it may show, or one shown twice;
 * - `questions`: a section with more questions than it may hold, or a research question's section
 *   with fewer;
 * - `serves`: a question naming a research question past the last or twice, or one in a research
 *   question's section that does not serve it;
 * - `options`: a poll with two answers worded alike;
 * - `minutes`: a guide past the shape's ceiling.
 */
export const GUIDE_ANSWER_RULES = [
  'document',
  'shape',
  'sections',
  'image',
  'questions',
  'serves',
  'options',
  'minutes',
] as const;

export type GuideAnswerRule = (typeof GUIDE_ANSWER_RULES)[number];

/**
 * How many sections a guide in this shape has around its research-question sections, and which
 * section index the first of those sits at.
 */
const shapeOffsets = (shape: GuideShape, rqCount: number) => {
  const first = shape.opening ? 1 : 0;

  return { first, total: first + rqCount + (shape.closing ? 1 : 0) };
};

/**
 * Whether the answer is a whole guide in the template's shape: the opening and closing sections the
 * shape asks for around one section per research question, in order, each holding between
 * `questionsPerRq.min` and `questionsPerRq.max` questions that serve it. An opening or closing section
 * holds at least one question and no more than a research question's section may, a placeholder
 * policy bound. Every position a question names is a research question in the list, named once; a
 * poll's answers are worded distinctly (`dgOptionWording`). Every image a question names is one of
 * the `imageCount` it may show, and none is named twice; an image it may show and names nowhere is
 * simply not shown. `null` for one that is, or else the rule it breaks first and where.
 */
const outOfShape = (
  answer: GuideAnswer,
  rqCount: number,
  shape: GuideShape,
  imageCount: number,
): { rule: GuideAnswerRule; at: string } | null => {
  const { first, total } = shapeOffsets(shape, rqCount);
  if (answer.sections.length !== total) {
    return { rule: 'sections', at: 'sections' };
  }

  const shown = new Set<number>();
  for (const [sectionIndex, section] of answer.sections.entries()) {
    for (const [questionIndex, { image }] of section.questions.entries()) {
      if (image === undefined) {
        continue;
      }
      if (image > imageCount || shown.has(image)) {
        return {
          rule: 'image',
          at: `sections.${String(sectionIndex)}.questions.${String(questionIndex)}.image`,
        };
      }
      shown.add(image);
    }
  }

  const { min, max } = shape.questionsPerRq;
  for (const [index, section] of answer.sections.entries()) {
    const position = index - first + 1;
    const forRq = position >= 1 && position <= rqCount;
    const at = `sections.${String(index)}.questions`;
    const count = section.questions.length;
    if (count > max || (forRq && count < min)) {
      return { rule: 'questions', at };
    }

    for (const [questionIndex, question] of section.questions.entries()) {
      if (
        question.serves.some((served) => served > rqCount) ||
        new Set(question.serves).size !== question.serves.length ||
        (forRq && !question.serves.includes(position))
      ) {
        return { rule: 'serves', at: `${at}.${String(questionIndex)}.serves` };
      }
      if (
        question.kind === 'poll' &&
        new Set(question.options.map(dgOptionWording)).size !== question.options.length
      ) {
        return { rule: 'options', at: `${at}.${String(questionIndex)}.options` };
      }
    }
  }

  return null;
};

const toQuestion = (
  question: GuideAnswerQuestion,
  rqIds: readonly string[],
  images: readonly DgStimulus[],
  mintId: () => string,
): DgQuestion => {
  const serves = question.serves.map((position) => rqIds[position - 1] as string);
  const stimulus = question.image === undefined ? undefined : images[question.image - 1];
  const shown = stimulus === undefined ? {} : { stimulus };

  return question.kind === 'open'
    ? {
        id: mintId(),
        type: 'open_response',
        text: question.text,
        serves_rq_ids: serves,
        worded_by: 'sigora',
        ...shown,
      }
    : {
        id: mintId(),
        type: 'single_choice',
        text: question.text,
        options: question.options.map((text) => ({ id: mintId(), text })),
        serves_rq_ids: serves,
        worded_by: 'sigora',
        ...shown,
      };
};

/**
 * The guide a model's answer drafts, or the rule it broke when it is not a whole guide.
 *
 * The whole answer must be one JSON document, optionally inside one code fence, which is the one
 * wrapping a model adds on its own. It must be in the template's shape (`outOfShape`), and it must fit
 * inside the shape's ceiling by `guideMinutes`, the seconds of every image it shows included.
 * Anything short of that is not an answer, so a partial guide can never be stored.
 *
 * Every id is minted here and every question is in Sigora's words: the model writes neither. The
 * research questions it names by position become the ids in `rqIds`, in that order, and the images
 * it names by number become the pinned `images`, in that order — so no file id ever comes from the
 * model, and a number past the last image or named twice makes the answer unreadable.
 */
export const parseGuideAnswer = (
  answer: string,
  {
    rqIds,
    shape,
    images,
    mintId,
  }: {
    rqIds: readonly string[];
    shape: GuideShape;
    /** The images the drafter may show, pinned to the Materials version it read, by number. */
    images: readonly DgStimulus[];
    mintId: () => string;
  },
): AnswerReading<DgContent, GuideAnswerRule> => {
  const read = readAnswerDocument(unfencedAnswer(answer));
  if (read === null) {
    return { ok: false, rule: 'document' };
  }
  const document = read.value;

  const parsed = guideAnswerSchema.safeParse(document);
  if (!parsed.success) {
    return refusedShape(parsed.error);
  }
  const refusal = outOfShape(parsed.data, rqIds.length, shape, images.length);
  if (refusal !== null) {
    return { ok: false, ...refusal };
  }

  const content: DgContent = {
    sections: parsed.data.sections.map((section) => ({
      id: mintId(),
      title: section.title,
      questions: section.questions.map((question) => toQuestion(question, rqIds, images, mintId)),
    })),
  };

  return guideMinutes(content) <= shape.maxMinutes
    ? { ok: true, value: content }
    : { ok: false, rule: 'minutes' };
};

/**
 * Why a settled request's guide was written, by the mode it answered: a first guide is drafted from
 * the research questions, a guide waiting on a change is reconciled, and a repair over a coverage
 * gap or uncertain line is redrafted to answer the research questions the check named.
 */
export const dgDraftReasonFor = (mode: DgDraftMode): ArtifactVersionReason =>
  mode === 'draft'
    ? 'drafted_from_questions'
    : mode === 'reconcile'
      ? 'reconciled'
      : 'redrafted_for_coverage';

type SampleKind = 'open' | 'poll';

/**
 * The layouts a sample research question's section is tried in, richest first: an open question
 * and a poll, then an open question alone, then polls alone, each padded with polls up to the
 * shape's minimum. Polls cost far less time, so the last one fits a ceiling the others do not.
 */
const sampleLayouts = (shape: GuideShape): SampleKind[][] => {
  const { min, max } = shape.questionsPerRq;
  const padded = (kinds: SampleKind[]): SampleKind[] => [
    ...kinds,
    ...Array.from({ length: Math.max(0, min - kinds.length) }, () => 'poll' as const),
  ];

  return [padded(['open', 'poll']), padded(['open']), padded(['poll'])].filter(
    (kinds) => kinds.length <= max,
  );
};

/** One research question's section in a sample guide, with questions of the given kinds. */
const sampleRqSection = (kinds: readonly SampleKind[]) => (position: number) => ({
  title: `Research question ${String(position)}`,
  questions: kinds.map((kind, index) =>
    kind === 'poll'
      ? {
          kind,
          text: `Which of these comes closest for you (${String(position)}.${String(index + 1)})?`,
          options: ['This one', 'That one', 'Neither'],
          serves: [position],
        }
      : {
          kind,
          text: `Tell us about your experience with this (${String(position)}.${String(index + 1)}).`,
          serves: [position],
        },
  ),
});

/**
 * A fixed guide in the template's shape for `rqIds`, for a stack with no model: the local fixture
 * route and the runtime checks settle it through the real request and settle. Each research
 * question's section takes the richest layout that keeps the whole guide inside the shape's ceiling
 * (`sampleLayouts`), images included. Each of `images` is shown once, in order, on the first
 * question of successive research-question sections; an image beyond the last of those is not
 * shown, and when no layout fits with every image shown the last is dropped, then the one before
 * it, until one does. Throws when no layout fits with no image at all.
 */
export const sampleGuideDraft = ({
  rqIds,
  shape,
  mintId,
  images,
}: {
  rqIds: readonly string[];
  shape: GuideShape;
  mintId: () => string;
  /** The images to show, pinned to the Materials version they are chosen from. */
  images: readonly DgStimulus[];
}): DgContent => {
  const everyRq = rqIds.map((_, index) => index + 1);
  const answerWith = (kinds: readonly SampleKind[]): GuideAnswerDocument => ({
    sections: [
      ...(shape.opening
        ? [
            {
              title: 'Getting started',
              questions: [
                {
                  kind: 'open' as const,
                  text: 'In your own words, what comes to mind first about this topic?',
                  serves: everyRq,
                },
              ],
            },
          ]
        : []),
      ...everyRq.map(sampleRqSection(kinds)),
      ...(shape.closing
        ? [
            {
              title: 'Anything else',
              questions: [
                {
                  kind: 'open' as const,
                  text: 'What should we have asked you that we did not?',
                  serves: everyRq,
                },
              ],
            },
          ]
        : []),
    ],
  });

  const { first } = shapeOffsets(shape, rqIds.length);
  const withImages = (content: DgContent, shown: readonly DgStimulus[]): DgContent => ({
    sections: content.sections.map((section, index) => {
      const stimulus = index >= first ? shown[index - first] : undefined;

      return stimulus === undefined || index - first >= rqIds.length
        ? section
        : {
            ...section,
            questions: section.questions.map((question, position) =>
              position === 0 ? { ...question, stimulus } : question,
            ),
          };
    }),
  });

  for (let count = Math.min(images.length, rqIds.length); count >= 0; count -= 1) {
    for (const kinds of sampleLayouts(shape)) {
      const content = parseGuideAnswer(JSON.stringify(answerWith(kinds)), {
        rqIds,
        shape,
        images: [],
        mintId,
      });
      const shown = content.ok ? withImages(content.value, images.slice(0, count)) : null;
      if (shown !== null && guideMinutes(shown) <= shape.maxMinutes) {
        return shown;
      }
    }
  }

  throw new Error('no sample guide fits the shape it was asked for');
};
